import { useState, useEffect, useCallback, useRef } from "react";
import { Button, Checkbox, Modal } from "@legal-500/legal500-design-system";
import { contacts as contactsService } from "@/api/contacts.service";
import { pipelineService, type Testimonial } from "@/api/pipeline.service";
import styles from "./LinkContactModal.module.css";

type ContactResult = {
  contactId: number;
  name: string;
  companyId: number | null;
  companyName: string | null;
};

type Props = {
  item: Testimonial;
  onClose: () => void;
  onLinked: (testimonial: Testimonial) => void;
};

export function LinkContactModal({ item, onClose, onLinked }: Props) {
  const [searchName, setSearchName] = useState(item.lawyerName ?? "");
  // firmRef is always non-null on testimonials, so default within-firm search to true
  const [withinFirm, setWithinFirm] = useState(true);
  const [results, setResults] = useState<ContactResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [linkingId, setLinkingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const initialSearchDone = useRef(false);

  const search = useCallback(
    async (name: string, scopedToFirm: boolean) => {
      if (!name.trim()) {
        setResults([]);
        return;
      }
      setSearching(true);
      setError(null);
      try {
        const firmRef = scopedToFirm && item.firmRef ? item.firmRef : undefined;
        const resp = await contactsService.getByName({ name, firmRef });
        setResults(resp.contacts);
      } catch {
        setError("Failed to search contacts. Please try again.");
        setResults([]);
      } finally {
        setSearching(false);
      }
    },
    [item.firmRef],
  );

  // Auto-search on mount — useRef guard prevents double-call in React StrictMode
  useEffect(() => {
    if (initialSearchDone.current) return;
    initialSearchDone.current = true;
    search(searchName, withinFirm);
  }, []);

  const handleNameChange = (value: string) => {
    setSearchName(value);
    search(value, withinFirm);
  };

  const handleWithinFirmChange = (checked: boolean) => {
    setWithinFirm(checked);
    search(searchName, checked);
  };

  const handleLink = async (contact: ContactResult) => {
    setLinkingId(contact.contactId);
    setError(null);
    try {
      const { testimonial } = await pipelineService.updateContactId(
        item.id,
        String(contact.contactId),
      );
      onLinked(testimonial);
    } catch {
      setError("Failed to link contact. Please try again.");
      setLinkingId(null);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Link Contact" size="medium" blurBackground>
      <div className={styles.body}>
        <div className={styles.searchRow}>
          <input
            className={styles.searchInput}
            type="text"
            value={searchName}
            onChange={e => handleNameChange(e.target.value)}
            placeholder="Search by name..."
            aria-label="Contact name"
          />
          <Checkbox
            checked={withinFirm}
            disabled={false}
            onChange={e => handleWithinFirmChange(e.target.checked)}
            label="Search within firm only"
          />
        </div>

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.results}>
          {searching && <p className={styles.hint}>Searching...</p>}
          {!searching && results.length === 0 && searchName.trim() && (
            <p className={styles.hint}>No contacts found.</p>
          )}
          {!searching && results.length > 0 && (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Company</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {results.map(contact => (
                  <tr key={contact.contactId}>
                    <td>{contact.name}</td>
                    <td>{contact.companyName ?? "—"}</td>
                    <td>
                      <Button
                        variant="outlined"
                        color="default"
                        size="small"
                        disabled={linkingId != null}
                        onClick={() => handleLink(contact)}
                      >
                        {linkingId === contact.contactId ? "Linking..." : "Link"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className={styles.footer}>
          <Button variant="outlined" color="default" onClick={onClose} disabled={linkingId != null}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
